import { ConstructionSiteList, PAGES } from "@dune/types";
import {
	Button,
	Card,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@dune/ui";
import { Users } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetConstructionSiteSubmissionParticipants } from "../../../../api/queries/constructionSiteSubmission.queries";
import { useDocumentSelection } from "../../../../hooks/panel-sync/useDocumentSelection";
import { useReceptionParticipantsView } from "../../../../hooks/useReceptionParticipantsView";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";
import { useMe } from "../../../../api/queries/users.queries";
import { RoleEnum } from "@dune/vars";
import { SubmissionParticipants } from "../../submission/SubmissionParticipants";
import ReceptionStep3Participants from "../../reception/steps/ReceptionStep3Participants";

interface ParticipantsTabProps {
	constructionSite: ConstructionSiteList;
}

const ParticipantsTab: React.FC<ParticipantsTabProps> = ({
	constructionSite,
}) => {
	const { constructionSiteId: constructionSiteIdString } = useRequiredParams<{
		constructionSiteId: string;
	}>();
	const { me: user } = useMe();
	const isInsurer = user?.role === RoleEnum.INSURER;
	const isDune = user?.role === RoleEnum.DUNE;
	const constructionSiteId = Number.parseInt(constructionSiteIdString);
	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState<"souscription" | "reception">(
		"souscription",
	);
	const handleTabChange = (value: string) =>
		setActiveTab(value as "souscription" | "reception");

	const {
		data: submissionData,
		isLoading,
		error,
	} = useGetConstructionSiteSubmissionParticipants(constructionSiteId);

	const receptionSections = useReceptionParticipantsView(constructionSiteId);

	useDocumentSelection({
		entityType: "CONSTRUCTION_SITE",
		entityId: constructionSiteId,
	});

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-6">
				<div className="animate-pulse">
					<div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
					<div className="h-32 bg-gray-200 rounded" />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col gap-6 p-6">
				<Card className="p-6">
					<h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
						<Users className="h-5 w-5 text-blue-600" />
						Intervenants
					</h3>
					<p className="text-red-500">
						Erreur lors du chargement des intervenants.
					</p>
				</Card>
			</div>
		);
	}

	const statusAllowsEdit =
		constructionSite.status === "NEW" ||
		constructionSite.status === "IN_PROGRESS" ||
		constructionSite.status === "TO_ANALYZE";

	return (
		<div className="h-full">
			<div className="flex flex-col gap-6">
				<div className="flex justify-between items-center">
					<div className="text-blue-badge-text text-2xl font-semibold">
						Intervenants
					</div>
					{(isDune ||
						(statusAllowsEdit &&
							!isInsurer &&
							!constructionSite.isSkipped)) && (
						<Button
							type="button"
							size="lg"
							onClick={() => {
								if (activeTab === "reception") {
									navigate(`/projects/${constructionSiteId}/reception?step=3`);
								} else {
									navigate(
										PAGES.PROJECTS.submission.withParams(
											constructionSiteId.toString(),
											4,
										),
									);
								}
							}}
						>
							Modifier les intervenants
						</Button>
					)}
				</div>

				<Tabs
					value={activeTab}
					onValueChange={handleTabChange}
					className="w-full"
				>
					<TabsList>
						<TabsTrigger value="souscription" variant="compact">
							Souscription
						</TabsTrigger>
						<TabsTrigger value="reception" variant="compact">
							Réception
						</TabsTrigger>
					</TabsList>

					<TabsContent value="souscription" className="mt-4">
						<div className="space-y-6">
							{submissionData ? (
								<SubmissionParticipants
									hideTitle
									disabledInputs
									disabledDocuments={!isDune}
								/>
							) : (
								<div className="text-center py-8">
									<Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
									<h3 className="text-lg font-semibold text-gray-600 mb-2">
										Aucun intervenant enregistré
									</h3>
									<p className="text-gray-500 mb-4">
										Commencez la soumission pour voir la liste des intervenants
									</p>
									<Button
										onClick={() => {
											navigate(
												PAGES.PROJECTS.submission.withParams(
													constructionSiteId.toString(),
													1,
												),
											);
										}}
										variant="outline"
									>
										Commencer la soumission
									</Button>
								</div>
							)}
						</div>
					</TabsContent>

					<TabsContent value="reception" className="mt-4">
						<div className="space-y-6">
							{constructionSite.constructionSiteDetails?.receptionDate &&
							receptionSections &&
							(receptionSections.designers.length > 0 ||
								receptionSections.builders.length > 0 ||
								receptionSections.materials.length > 0) ? (
								<ReceptionStep3Participants
									hideTitle
									disabledInputs
									disabledDocuments={!isDune}
								/>
								// <>
								// 	<DesignersTableReception
								// 		submissionParticipants={receptionSections.designers}
								// 		constructionSiteId={constructionSiteId}
								// 		selectedParticipantId={selectedParticipantId}
								// 		onParticipantSelect={handleParticipantSelect}
								// 		onDocumentClick={handleDocumentClick}
								// 		loadingDocumentId={loadingDocumentId}
								// 		displayInsurer
								// 	/>
								// 	<Separator className="my-8" />
								// 	<BuildersTableReception
								// 		submissionParticipants={receptionSections.builders}
								// 		constructionSiteId={constructionSiteId}
								// 		selectedParticipantId={selectedParticipantId}
								// 		onParticipantSelect={handleParticipantSelect}
								// 		onDocumentClick={handleDocumentClick}
								// 		loadingDocumentId={loadingDocumentId}
								// 		displayInsurer
								// 	/>
								// 	<Separator className="my-8" />
								// 	<MaterialsTableReception
								// 		submissionParticipants={receptionSections.materials}
								// 		constructionSiteId={constructionSiteId}
								// 		selectedParticipantId={selectedParticipantId}
								// 		onParticipantSelect={handleParticipantSelect}
								// 		onDocumentClick={handleDocumentClick}
								// 		loadingDocumentId={loadingDocumentId}
								// 	/>
								// </>
							) : (
								<div className="text-center py-8">
									<Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
									<h3 className="text-lg font-semibold text-gray-600 mb-2">
										Aucun intervenant de réception
									</h3>
									<p className="text-gray-500 mb-4">
										Les intervenants de soumission seront copiés ici
										automatiquement à l'ouverture
									</p>
								</div>
							)}
						</div>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
};

export default ParticipantsTab;
